/**
 * Team Trips — Customs Stamps Alpine component
 *
 * Auto-rotates the active stamp index every 3.4s (A13 in SPEC_motion.md).
 * The active stamp gets scale(1.16) and an alternating drop-shadow glow
 * animation (A14, A15) driven by the `tt-cstamp active` CSS classes.
 *
 * Pause on hover or focus-within (accessible: keyboard users can tab to a
 * stamp button and pause the rotation, then navigate with arrow keys).
 *
 * Disabled entirely when `prefers-reduced-motion: reduce` is active.
 */
export default function customsStamps(totalStamps = 8) {
    const INTERVAL_MS = 3400;

    return {
        /** 1-based index of the active stamp (matches day numbers in templates). */
        activeStamp: 1,

        /** Whether auto-rotation is currently paused (hover/focus). */
        paused: false,

        /** Internal interval handle. */
        _interval: null,

        /** True when the browser has requested reduced motion. */
        _reduced: typeof window !== 'undefined'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches,

        init() {
            if (this._reduced) {
                return;
            }

            this._interval = setInterval(() => {
                if (!this.paused) {
                    this.activeStamp = (this.activeStamp % totalStamps) + 1;
                }
            }, INTERVAL_MS);
        },

        destroy() {
            if (this._interval !== null) {
                clearInterval(this._interval);
                this._interval = null;
            }
        },

        /**
         * Manually select a stamp (called by stamp button clicks or dropdown
         * row selection from a sibling Alpine component / Livewire action).
         *
         * @param {number} stampIndex 1-based index
         */
        select(stampIndex) {
            this.activeStamp = stampIndex;
        },

        /**
         * Returns true when the given stamp index is currently active.
         * Use in templates as: `:class="{ active: stamps.isActive(i) }"`.
         *
         * @param {number} index 1-based index
         * @returns {boolean}
         */
        isActive(index) {
            return this.activeStamp === index;
        },
    };
}
